// ID token lives in memory only — XSS-attainable but never persisted, which
// keeps the worst-case blast-radius bounded by the tab's lifetime.
//
// Refresh token lives in localStorage. The honest framing: any JS that runs in
// our origin can read both — so we don't pretend httpOnly cookies are achievable
// from a static SPA. The trade we DO make: refresh token persisting across
// tabs/PWA cold-starts so the user isn't bounced to Cognito every time.

// SecureStore key namespace — renamed from `recallly.*` to `jotly.*` as part of
// the Jotly rebrand. Existing testers will be signed out on first reload of
// the rebranded build (their old keys are orphaned but harmless). Acceptable
// per the hard-cut rebrand posture — no production users to migrate yet.
const REFRESH_KEY = 'jotly.refreshToken';
const EXP_KEY = 'jotly.idTokenExp';

let idToken: string | null = null;
let idTokenExp = 0; // epoch ms

type InvalidationListener = () => void;
const listeners = new Set<InvalidationListener>();

export function setTokens(args: {
  idToken: string;
  refreshToken?: string;
  expiresInSeconds: number;
}) {
  idToken = args.idToken;
  idTokenExp = Date.now() + args.expiresInSeconds * 1000;
  if (args.refreshToken) {
    try {
      localStorage.setItem(REFRESH_KEY, args.refreshToken);
    } catch {
      // Some Safari configurations throw on storage access — degrade silently.
    }
  }
  try {
    localStorage.setItem(EXP_KEY, String(idTokenExp));
  } catch { /* see above */ }
}

export function readIdTokenInMemory(): { token: string | null; expMs: number } {
  return { token: idToken, expMs: idTokenExp };
}

export function readRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

export function clearTokens() {
  idToken = null;
  idTokenExp = 0;
  try {
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(EXP_KEY);
  } catch { /* noop */ }
  listeners.forEach((l) => {
    try { l(); } catch { /* swallow listener errors */ }
  });
}

export function onAuthInvalidated(listener: InvalidationListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function hasRefreshToken(): boolean {
  return !!readRefreshToken();
}

// ─── Account-blocked broadcast ────────────────────────────────────────────────
// The API client raises AccountBlockedError on 403 from the BLOCKED gate; this
// channel lets AuthContext flip to a "your account is suspended" screen
// without every consumer having to inspect query errors individually.
const blockedListeners = new Set<(message: string) => void>();

export function notifyAccountBlocked(message: string): void {
  blockedListeners.forEach((l) => {
    try { l(message); } catch { /* swallow listener errors */ }
  });
}

export function onAccountBlocked(listener: (message: string) => void): () => void {
  blockedListeners.add(listener);
  return () => blockedListeners.delete(listener);
}
